<script lang="ts">
  import { api } from "$lib/api/client";
  import FileUpload from "$lib/components/FileUpload.svelte";
  import CategoryPicker from "$lib/components/CategoryPicker.svelte";
  import AccountPicker from "$lib/components/AccountPicker.svelte";
  import PaywallModal from "$lib/components/PaywallModal.svelte";
  import InstallmentFields from "$lib/components/InstallmentFields.svelte";
  import RecurrenceFields from "$lib/components/RecurrenceFields.svelte";
  import { accounts } from "$lib/stores/accounts.svelte";
  import { categories } from "$lib/stores/categories.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { todayISO } from "$lib/utils/format";

  let { onCreated, edit = null, scope = "one" } = $props<{
    onCreated: () => void;
    edit?: any | null;
    scope?: string;
  }>();

  type Mode = "single" | "installments" | "recurring";

  // Snapshot do prop "edit" no momento da montagem (modal recria a instância
  // a cada abertura, então não precisamos reagir a mudanças posteriores).
  // svelte-ignore state_referenced_locally
  const initial: any = edit ? { ...edit } : null;
  const isEdit = !!initial;

  let mode = $state<Mode>("single");
  let kind = $state<"income" | "expense" | "transfer">(initial?.kind ?? "expense");
  let date = $state<string>(initial?.date ?? todayISO());
  let due_date = $state<string>(initial?.due_date ?? "");
  let description = $state<string>(initial?.description ?? "");
  let amount = $state<string>(
    initial?.amount != null ? String(initial.amount).replace(".", ",") : "",
  );
  let status = $state<"pending" | "effected">(
    initial?.status === "effected" ? "effected" : "pending",
  );
  let privacyField = $state<"default" | "owner_only">(
    initial?.privacy === "owner_only" ? "owner_only" : "default",
  );
  let category_id = $state<string | null>(initial?.category_id ?? null);
  let account_id = $state<string | null>(initial?.account_id ?? null);

  let installmentCount = $state(2);
  let installmentInterval = $state(1);

  let recFrequency = $state<"daily" | "weekly" | "monthly" | "yearly">("monthly");
  let recInterval = $state(1);
  let recEndAt = $state("");

  let boletoFile = $state<File | null>(null);
  let comprovanteFile = $state<File | null>(null);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let paywallOpen = $state(false);
  let paywallReason = $state<"transactions" | "limit">("limit");


  async function uploadAttachment(file: File, purpose: string): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const r = await api<{ id: string }>("/attachments", { method: "POST", body: fd });
    return r.id;
  }

  function buildBase() {
    const amountNumeric = String(amount).replace(/\./g, "").replace(",", ".");
    // Data: usa o vencimento como data de referência; se não houver, usa hoje
    const resolvedDate = due_date || todayISO();
    return {
      kind, date: resolvedDate,
      due_date: due_date || null,
      description: description.trim(),
      amount: amountNumeric,
      status,
      privacy: privacyField,
      currency: "BRL",
      category_id,
      account_id,
    } as Record<string, unknown>;
  }

  async function submit(e: Event) {
    e.preventDefault();
    error = null;

    if (!description.trim()) { error = "Descreva a transação."; return; }
    if (!amount || Number(String(amount).replace(",", ".")) <= 0) { error = "Informe um valor."; return; }
    if (!category_id) { error = "Escolha uma categoria."; return; }
    if (!account_id) {
      const a = await accounts.ensureDefault();
      account_id = a.id;
    }

    saving = true;
    try {
      const base = buildBase();
      if (boletoFile) base.boleto_attachment_id = await uploadAttachment(boletoFile, "boleto");
      if (comprovanteFile) base.comprovante_attachment_id = await uploadAttachment(comprovanteFile, "comprovante");

      if (isEdit) {
        const patch: Record<string, unknown> = {
          description: base.description,
          amount: base.amount,
          date: base.date,   // resolvido automaticamente
          due_date: base.due_date,
          status: base.status,
          category_id: base.category_id,
          account_id: base.account_id,
        };
        if (base.boleto_attachment_id) patch.boleto_attachment_id = base.boleto_attachment_id;
        if (base.comprovante_attachment_id) patch.comprovante_attachment_id = base.comprovante_attachment_id;
        patch.privacy = base.privacy;
        await api(`/transactions/${initial.id}?scope=${scope}`, { method: "PATCH", body: JSON.stringify(patch) });
        toasts.success("Transação atualizada");
        onCreated();
        return;
      }

      if (mode === "single") {
        await api("/transactions", { method: "POST", body: JSON.stringify(base) });
        toasts.success("Transação registrada");
      } else if (mode === "installments") {
        await api("/transactions/installments", {
          method: "POST",
          body: JSON.stringify({
            template: base,
            count: installmentCount,
            frequency: "monthly",
            interval: installmentInterval,
          }),
        });
        toasts.success(`${installmentCount} parcelas criadas`);
      } else {
        await api("/recurrences", {
          method: "POST",
          body: JSON.stringify({
            template: base,
            frequency: recFrequency,
            interval: recInterval,
            next_run_at: new Date(date + "T09:00:00").toISOString(),
            end_at: recEndAt ? new Date(recEndAt + "T09:00:00").toISOString() : null,
          }),
        });
        await api("/transactions", { method: "POST", body: JSON.stringify(base) });
        toasts.success("Recorrência cadastrada");
      }
      onCreated();
    } catch (err: any) {
      const msg = err.body?.detail || err.message || "Erro";
      if (msg === "quota_transactions_exceeded" || err.body?.code === "quota_transactions_exceeded") {
        paywallReason = "transactions"; paywallOpen = true;
      } else {
        error = msg;
      }
    } finally { saving = false; }
  }
</script>

<form onsubmit={submit} class="card space-y-4">
  {#if error}
    <div role="alert" class="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">{error}</div>
  {/if}

  {#if !isEdit}
  <div class="flex gap-2">
    <button type="button" class="flex-1 {kind === 'expense' ? 'btn-primary' : 'btn-secondary'}"
            onclick={() => (kind = 'expense')}>💸 Despesa</button>
    <button type="button" class="flex-1 {kind === 'income' ? 'btn-primary' : 'btn-secondary'}"
            onclick={() => (kind = 'income')}>💰 Receita</button>
    <button type="button" class="flex-1 {kind === 'transfer' ? 'btn-primary' : 'btn-secondary'}"
            onclick={() => (kind = 'transfer')}>🔁 Transferência</button>
  </div>
  {/if}

  {#if !isEdit}
  <div role="tablist" class="flex gap-2 text-sm" aria-label="Modo de lançamento">
    <button type="button" role="tab" aria-selected={mode === "single"}
            class="flex-1 {mode === 'single' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => (mode = 'single')}>Única</button>
    <button type="button" role="tab" aria-selected={mode === "installments"}
            class="flex-1 {mode === 'installments' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => (mode = 'installments')}>Parcelada</button>
    <button type="button" role="tab" aria-selected={mode === "recurring"}
            class="flex-1 {mode === 'recurring' ? 'btn-primary' : 'btn-ghost'}"
            onclick={() => (mode = 'recurring')}>Recorrente</button>
  </div>
  {/if}

  <div>
    <label class="label" for="desc">Descrição</label>
    <input id="desc" class="input" bind:value={description} required maxlength="280"
           placeholder={kind === "expense" ? "Ex.: Mercado da semana" : "Ex.: Salário"} />
  </div>

  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <label class="label" for="amt">Valor {mode === "installments" ? "da parcela" : ""} (R$)</label>
      <input id="amt" inputmode="decimal" class="input" bind:value={amount} required placeholder="0,00" />
    </div>
    <div>
      <label class="label" for="cat">Categoria</label>
      <CategoryPicker bind:value={category_id} kind={kind === "income" ? "income" : "expense"} required />
    </div>
  </div>

  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div>
      <label class="label" for="d2">Vencimento</label>
      <input id="d2" type="date" class="input" bind:value={due_date} />
    </div>
    <div>
      <label class="label" for="acc">Conta</label>
      <AccountPicker bind:value={account_id} />
    </div>
  </div>

  <div>
    <label class="label" for="st">Situação</label>
    <select id="st" class="input" bind:value={status}>
      <option value="pending">Pendente (a pagar/receber)</option>
      <option value="effected">Já paga / recebida</option>
    </select>
  </div>

  <div class="flex items-center justify-between gap-3 rounded-lg border p-3" style="border-color: var(--border)">
    <div class="flex items-center gap-2">
      <span aria-hidden="true" class="text-lg">{privacyField === "owner_only" ? "🔒" : "🔓"}</span>
      <div>
        <div class="text-sm font-medium">Privacidade</div>
        <div class="text-xs text-muted">
          {privacyField === "owner_only"
            ? "Só visível para o dono na camada privada."
            : "Visível para todos com acesso a este workspace."}
        </div>
      </div>
    </div>
    <button
      type="button"
      class="btn-ghost text-sm"
      aria-pressed={privacyField === "owner_only"}
      onclick={() => (privacyField = privacyField === "owner_only" ? "default" : "owner_only")}
    >
      {privacyField === "owner_only" ? "Tornar pública" : "Marcar como privada"}
    </button>
  </div>

  {#if !isEdit && mode === "installments"}
    <InstallmentFields bind:installmentCount bind:installmentInterval />
  {/if}

  {#if !isEdit && mode === "recurring"}
    <RecurrenceFields bind:recFrequency bind:recInterval bind:recEndAt />
  {/if}

  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 divider pt-3">
    <FileUpload bind:file={boletoFile} label="Boleto / Cobrança"
                hint="Obrigatório para empresas + despesa." />
    <FileUpload bind:file={comprovanteFile} label="Comprovante"
                hint="Obrigatório para marcar como paga (empresa + despesa)." />
  </div>

  <div class="flex justify-end gap-2">
    <button class="btn-primary" disabled={saving}>
      {#if saving}Salvando…
      {:else if isEdit}Salvar alterações
      {:else if mode === "single"}Salvar
      {:else if mode === "installments"}Criar {installmentCount} parcelas
      {:else}Criar recorrência{/if}
    </button>
  </div>
</form>

<PaywallModal bind:open={paywallOpen} reason={paywallReason} />
